// Locked while it is sealed. Locked is not a folder with a padlock painted on it: sealed, its
// contents are drawn as measured redaction bars, since sizes are all that is legible until it is
// unlocked — names, contents and dates are not. The rows are drawn rather than hidden, so a Locked
// that holds things never looks like an empty or broken one.
//
// Before there is a passphrase there is nothing to seal yet, and the screen says what Locked is for
// and offers to set one. Once the passphrase is taken, the screen says Locked is unlocking until
// what it holds comes back, rather than offering to unlock it again.
import PropTypes from 'prop-types'

import FolderLockIcon from '@mui/icons-material/FolderSpecialOutlined'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import ShieldIcon from '@mui/icons-material/ShieldOutlined'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {formatBytes} from '../../lib/fileTools'

import './lockedSeal.scss'

// A bar's width stands for a name without giving its length away: folders all alike, files in a
// few steps that follow their place in the list rather than anything about them.
const barWidth = (kind, index) => (kind === 'folder' ? 86 : 108 + (index % 5) * 22)

const SealedRow = ({kind, filesize, index}) => (
  <div className='sealed-row'>
    {kind === 'folder' ? <FolderLockIcon className='sealed-glyph' /> : <LockIcon className='sealed-glyph' />}
    <span className='redaction' style={{width: barWidth(kind, index)}} aria-label={`Sealed ${kind}`} />
    <span className='sealed-size'>{typeof filesize === 'number' ? formatBytes(filesize) : '—'}</span>
    <span className='sealed-modified'>—</span>
  </div>
)

SealedRow.propTypes = {
  kind: PropTypes.oneOf(['folder', 'file']).isRequired,
  filesize: PropTypes.number,
  index: PropTypes.number.isRequired
}

const LockedSeal = ({status, items, unlocking = false, onUnlock, onSetPassphrase}) => {
  if (status === 'unset') {
    return (
      <div className='locked-seal'>
        <ShieldIcon className='seal-mark' />
        <h2 className='seal-title'>Nothing is locked yet</h2>
        <Text className='seal-body'>
          Items you move into Locked are sealed with a passphrase only you know, and stay unreadable until you unlock it.
        </Text>
        <BaseButton className='seal-action' color='secondary' onClick={onSetPassphrase}>
          <ShieldIcon fontSize='small' />Set a passphrase
        </BaseButton>
      </div>
    )
  }

  return (
    <>
      <div className='locked-seal'>
        <ShieldIcon className='seal-mark' />
        <h2 className='seal-title'>{unlocking ? 'Unlocking…' : 'Locked is sealed'}</h2>
        {unlocking ?
          <Text className='seal-body' role='status'>Opening what Locked holds. This can take a moment.</Text> :
          (
            <Text className='seal-body'>
              {items.length} item{items.length === 1 ? ' is' : 's are'} here. Their sizes are all that is legible until you unlock —
              names, contents and dates are not.
            </Text>
          )}
        <BaseButton className='seal-action' color='secondary' disabled={unlocking} onClick={onUnlock}>
          <UnlockIcon fontSize='small' />{unlocking ? 'Unlocking…' : 'Unlock'}
        </BaseButton>
      </div>
      {items.length > 0 && (
        <section className='sealed-items'>
          <div className='sealed-note'><InfoIcon fontSize='small' />What the drive can still tell you</div>
          {items.map(({kind, filesize}, index) => <SealedRow key={index} kind={kind} filesize={filesize} index={index} />)}
        </section>
      )}
    </>
  )
}

LockedSeal.propTypes = {
  status: PropTypes.oneOf(['unset', 'sealed']).isRequired,
  // One for each item at Locked's top, with a file's size.
  items: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.oneOf(['folder', 'file']).isRequired,
    filesize: PropTypes.number
  })).isRequired,
  // The passphrase was taken and what Locked holds is on its way.
  unlocking: PropTypes.bool,
  onUnlock: PropTypes.func.isRequired,
  onSetPassphrase: PropTypes.func.isRequired
}

export default LockedSeal
